import type { Database, DatabaseReference, DataSnapshot } from 'firebase/database';
import { getDatabase, off, onValue, ref } from 'firebase/database';
import { isNumber } from 'lodash';
import { makeAutoObservable, reaction, runInAction } from 'mobx';

import getEnvironment from '../../config/environment';
import firebaseApp from '../../config/firebase';
import { createSessionData, createSessionState, getDeviceCount } from './helper';
import Paths from './paths';
import type { ISessionData, ISessionState } from './types';
import { SessionStatus } from './types';
import type { IDataSourceFirebaseStore } from './useDataSourceFirebase/types';

export interface IFirebaseSessionStore {
  sessionState: Partial<ISessionState>;
  isLoading: boolean;
  currentSessionKey: string | null;
  setSessionInfo(sessionKey: string, clinicId: string, deviceId: string): void;
  clearSessionInfo(): void;
  setLoading(loading: boolean): void;
  isLeaderDevice(deviceId?: string): boolean;
}

const defaultSessionState = {
  sessionData: null,
  status: SessionStatus.INACTIVE,
} as Partial<ISessionState>;

export class FirebaseSessionStore implements IFirebaseSessionStore {
  sessionState = defaultSessionState;
  isLoading = false;
  _currentSessionKey: string | null = null;
  private _currentSessionRef: DatabaseReference | null = null;
  private db: Database | undefined;
  private clinicId: string = '';
  private deviceId: string = '';

  private recentStartedDeviceId: string = ''; //this id to control loading, and showing toast in case of observe session

  constructor(private dataSourceFirebaseStore: IDataSourceFirebaseStore) {
    makeAutoObservable(this);
    this.initializeDatabase();
    reaction(
      () => this._currentSessionKey,
      (newSessionKey, oldSessionKey) => {
        try {
          // End session if old session key is removed
          if (!newSessionKey && oldSessionKey) {
            this.clearSessionData();
            this.stopListening();
          }
          // Start new session if the session key changes
          if (newSessionKey && newSessionKey !== oldSessionKey) {
            this.listenToDatabase();
          }
        } catch (error) {
          console.error(
            `Error handling session key change: ${oldSessionKey} -> ${newSessionKey}`,
            error,
          );
        }
      },
    );
  }

  private initializeDatabase() {
    try {
      if (!firebaseApp) {
        throw new Error('Firebase app is not initialized');
      }
      this.db = getDatabase(firebaseApp);
    } catch (error) {
      console.error('Failed to initialize Firebase Database', error);
    }
  }

  private getSessionData(snapshot: DataSnapshot) {
    const [clinicianId, patientId] = (this.currentSessionKey ?? '').split('|');
    const startTime = snapshot.child('startTime').val() || 0;
    const metadata = snapshot.child('metadata').val() || {};

    if (!getEnvironment().isProd && !startTime) console.warn('Session data missing startTime');
    const activeDevices = Object.fromEntries(
      Object.entries(snapshot.child('devices').val() || {}).map(([key, value]) => [
        key,
        isNumber(value) ? value : 0,
      ]),
    ) as Record<string, number>;
    return createSessionData({
      clinicianId,
      patientId,
      startTime,
      metadata,
      activeDevices,
    });
  }

  private async handleDeviceUpdates(snapshot: DataSnapshot, sessionRef: DatabaseReference) {
    const deviceNumber = snapshot.child(`devices/${this.deviceId}`).val();
    if (this.deviceId && isNumber(deviceNumber)) {
      const isActiveDevice = this.deviceId === this.recentStartedDeviceId;
      this.recentStartedDeviceId = ''; // Reset current device ID temporarily
      await this.dataSourceFirebaseStore.handleSessionLeadership(
        sessionRef,
        deviceNumber,
        this.clinicId,
        isActiveDevice,
      );
    }
  }

  // Handle device count change
  private async handleDeviceCountChanges(currentDeviceCount: number, lastDeviceCount: number) {
    if (currentDeviceCount !== lastDeviceCount && this.currentSessionKey && this.deviceId) {
      try {
        await this.dataSourceFirebaseStore.setupDisconnectHandlers(
          this.currentSessionKey,
          this.deviceId,
          this.clinicId,
        );
      } catch (error) {
        console.error('Error setting up disconnect handlers:', error);
      }
    }
  }

  private listenToDatabase() {
    if (!this.currentSessionKey || !this.clinicId || !this.db) {
      if (!getEnvironment().isProd) console.warn('session info is null or undefined');
      return;
    }
    const sessionRef = ref(this.db, Paths.activeSession(this.clinicId, this.currentSessionKey));

    /**
     * Listens to real-time database changes for the current session.
     * - Updates the session state when data changes.
     * - Monitors active devices and sets up disconnect handlers when the device count changes.
     * - Checks the device number and handles session leadership if needed.
     * - Logs errors if the database listener encounters an issue.
     */
    onValue(
      sessionRef,
      async (snapshot) => {
        if (snapshot.exists()) {
          if (!getEnvironment().isProd) console.log(`1-Database updated:`, snapshot.val());
          if (this.recentStartedDeviceId) this.setLoading(true);
          const lastDeviceCount = getDeviceCount(this.sessionState.sessionData as ISessionData); // Cache old count
          const sessionData = this.getSessionData(snapshot);
          runInAction(() => {
            // Update session state
            this.sessionState = {
              ...this.sessionState,
              ...createSessionState(this.deviceId, {
                sessionData: sessionData,
              }),
            };
          });
          const currentDeviceCount = getDeviceCount(this.sessionState.sessionData as ISessionData);
          if (!getEnvironment().isProd) {
            console.log('2-activeDevices-count', currentDeviceCount);
            console.log('3-last', lastDeviceCount, 'new', currentDeviceCount);
          }
          await this.handleDeviceCountChanges(currentDeviceCount, lastDeviceCount);
          await this.handleDeviceUpdates(snapshot, sessionRef);
          this.setLoading(false);
        }
      },
      (error) => {
        console.error(`Error listening to sessionKey: ${this.currentSessionKey}`, error);
        this.setLoading(false);
      },
    );

    this._currentSessionRef = sessionRef; // Save the reference for cleanup
  }

  private stopListening() {
    if (this._currentSessionRef) {
      off(this._currentSessionRef);
      this._currentSessionRef = null;
    }
  }

  private clearSessionData() {
    this.sessionState = defaultSessionState;
    this.isLoading = false;
  }

  clearSessionInfo() {
    this.currentSessionKey = null;
  }

  setSessionInfo(sessionKey: string, clinicId: string, deviceId: string) {
    this.currentSessionKey = sessionKey;
    this.clinicId = clinicId;
    this.deviceId = deviceId;
    this.recentStartedDeviceId = deviceId;
  }

  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  get currentSessionKey() {
    return this._currentSessionKey;
  }

  set currentSessionKey(newKey: string | null) {
    this._currentSessionKey = newKey;
  }

  isLeaderDevice(deviceId?: string): boolean {
    const currentDeviceId = deviceId || this.deviceId;
    return this.sessionState.sessionData?.isDeviceMain?.(currentDeviceId) ?? false;
  }
}
